import { useTranslation } from "react-i18next";

import type { ImportPreviewError } from "@/lib/admin-api";

/**
 * apps/web :: components/admin :: ImportErrorTable
 *
 * Renders per-row CSV-import validation errors. Errors arrive flat
 * from the backend (one entry per field-level failure) and are
 * grouped here by `rowNumber` so the admin can scan one offending
 * row at a time. Wrapped in `aria-live="polite"` so screen-readers
 * announce changes when the preview re-runs.
 */
export interface ImportErrorTableProps {
  errors: ImportPreviewError[];
}

export default function ImportErrorTable({ errors }: ImportErrorTableProps) {
  const { t } = useTranslation();

  if (errors.length === 0) {
    return null;
  }

  // Group by row number so the admin sees "row 5: 3 issues" together.
  const grouped = new Map<number, ImportPreviewError[]>();
  for (const err of errors) {
    const list = grouped.get(err.rowNumber) ?? [];
    list.push(err);
    grouped.set(err.rowNumber, list);
  }
  const ordered = Array.from(grouped.entries()).sort(([a], [b]) => a - b);

  return (
    <section
      aria-live="polite"
      data-testid="admin-import-errors"
      className="rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
    >
      <h3 className="mb-sm text-base font-semibold">
        {t("admin.import.errors.heading", { count: errors.length })}
      </h3>
      <ul className="space-y-sm">
        {ordered.map(([rowNumber, list]) => (
          <li
            key={rowNumber}
            data-testid={`admin-import-error-row-${rowNumber}`}
            className="rounded-md border border-danger/20 bg-white p-sm"
          >
            <p className="font-semibold text-danger">
              {t("admin.import.errors.rowLabel", { row: rowNumber })}
            </p>
            <ul className="mt-xs list-disc pl-md text-xs text-neutral-800">
              {list.map((err, idx) => (
                <li key={`${err.field}-${idx}`}>
                  <span className="font-mono">{err.field}</span>: {err.message}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}
